<template>
  <section class="proceso">
    <DsChip label="Tu proceso" variant="orange" />

    <div class="proceso__header">
      <DsTextSplit
        as="h3"
        base="Así avanza"
        highlight="tu proceso"
        baseColor="var(--ds-purple-900)"
        highlightColor="#6D59EA"
      />
      <DsText as="body3" class="proceso__intro">
        Tu camino está dividido en etapas claras. Aquí puedes entender en cuál estás y qué sigue:
      </DsText>
    </div>

    <div class="proceso__grid">
      <article class="proceso__current">
        <span class="proceso__chip proceso__chip--success">Etapa actual</span>

        <div class="proceso__stage">
          <div class="proceso__number proceso__number--current">1</div>
          <div class="proceso__stage-copy">
            <DsText as="subtitle5" color="var(--ds-secondary-main)">Contratación</DsText>
            <DsText as="body3" class="proceso__stage-desc">Ya iniciaste tu proceso.</DsText>
          </div>
        </div>

        <div class="proceso__divider" />

        <div class="proceso__details">
          <DsText as="subtitle7" color="var(--ds-secondary-main)">¿Qué sucede en esta etapa?</DsText>
          <DsText as="body3" class="proceso__details-text">
            Registramos tu solicitud y comenzamos la evaluación inicial de tu expediente.
          </DsText>
        </div>
      </article>

      <article v-for="stage in stages" :key="stage.number" class="proceso__stage-card">
        <div class="proceso__stage proceso__stage--compact">
          <div class="proceso__number">{{ stage.number }}</div>
          <div class="proceso__stage-copy">
            <DsText as="subtitle7" color="var(--ds-secondary-main)">{{ stage.title }}</DsText>
            <DsText as="body3" class="proceso__stage-desc">{{ stage.description }}</DsText>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup lang="ts">
import DsChip from '../atoms/DsChip.vue'
import DsText from '../atoms/DsText.vue'
import DsTextSplit from '../atoms/DsTextSplit.vue'

const stages = [
  {
    number: 2,
    title: 'Asesoría',
    description: 'Recibes acompañamiento para tomar decisiones.',
  },
  {
    number: 3,
    title: 'Adjudicación',
    description: 'Aquí puedes obtener tu financiamiento.',
  },
  {
    number: 4,
    title: 'Escrituración',
    description: 'Formalizas la adquisición de tu inmueble.',
  },
  {
    number: 5,
    title: 'Liquidación',
    description: 'Concluyes tu proceso y recuperas tu garantía.',
  },
]
</script>

<style scoped>
.proceso {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.proceso__header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.proceso__intro {
  color: var(--ds-text-primary);
  line-height: 1.6;
}

.proceso__grid {
  display: grid;
  grid-template-columns: 1.18fr 1fr 1fr;
  grid-template-rows: repeat(2, minmax(154px, auto));
  gap: 18px;
  align-items: stretch;
}

.proceso__current{
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #AB9FF3;
}

.proceso__stage-card {
  background: #FBFBFB;
  border-radius: 8px;
  border: 1px solid rgba(47, 45, 116, 0.14);
  box-shadow: 0 1px 0 rgba(47, 45, 116, 0.03);
}

.proceso__current {
  padding: 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  grid-column: 1;
  grid-row: 1 / span 2;
}

.proceso__chip {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  align-self: flex-start;
  border-radius: 999px;
  min-height: 40px;
  padding: 8px 16px;
  box-sizing: border-box;
  font-family: var(--ds-font-family-base);
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.proceso__chip--success {
  background: #e4f3e8;
  color: #137c35;
}

.proceso__chip--success::before {
  content: '';
  display: block;
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #118237;
}

.proceso__stage {
  display: flex;
  align-items: center;
  gap: 16px;
}

.proceso__stage--compact {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  align-items: center;
  column-gap: 16px;
  padding: 20px 18px;
}

.proceso__number {
  width: 46px;
  height: 46px;
  min-width: 46px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(73, 47, 229, 0.12);
  color: var(--ds-secondary-main);
  font-family: var(--ds-font-family-base);
  font-size: 22px;
  font-weight: 600;
  align-self: center;
}

.proceso__number--current {
  width: 52px;
  height: 52px;
  min-width: 52px;
  background: #e6e2ff;
  font-size: 24px;
}

.proceso__stage-copy {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.proceso__stage-desc,
.proceso__details-text {
  color: var(--ds-text-primary);
  line-height: 1.35;
}

.proceso__divider {
  height: 1px;
  background: rgba(47, 45, 116, 0.16);
}

.proceso__details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.proceso__stage-card {
  min-height: 100px;
}

.proceso__stage-card .proceso__stage-copy {
  max-width: none;
}

@media (max-width: 1024px) {
  .proceso__grid {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
  }

  .proceso__current {
    grid-column: 1 / -1;
    grid-row: auto;
  }
}

@media (min-width: 1025px) {
  .proceso { gap: 32px; }
  .proceso__intro { line-height: 24px; }
  .proceso__grid {
    grid-template-columns: 305px repeat(2, minmax(0, 1fr));
    grid-template-rows: repeat(2, 120px);
    gap: 45px 24px;
  }
  .proceso__current {
    height: 285px;
    padding: 24px;
    gap: 16px;
    box-sizing: border-box;
  }
  .proceso__chip {
    min-height: 32px;
    padding: 8px;
    gap: 4px;
    font-size: 12px;
    line-height: 16px;
  }
  .proceso__chip--success::before {
    width: 12px;
    height: 12px;
    flex-basis: 12px;
  }
  .proceso__number--current { width: 48px; height: 48px; min-width: 48px; }
  .proceso__number { width: 40px; height: 40px; min-width: 40px; font-family: var(--ds-font-family-bebas); font-size: 24px; }
  .proceso__stage--compact { grid-template-columns: 41px minmax(0, 1fr); gap: 8px; padding: 16px; }
  .proceso__stage-card { min-height: 120px; }
  .proceso__details { gap: 8px; }
  .proceso__stage-desc,
  .proceso__details-text { line-height: normal; }
}

@media (max-width: 768px) {
  .proceso { gap: 40px; }
  .proceso__grid {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    gap: 24px;
  }
}
</style>
